// ═══════════════════════════════════════════════════════════════
// COMPONENTES REUTILIZABLES para flujo de firma de documentos
// (Devoluciones, No Conformidades, Proveedores, Formación)
// ═══════════════════════════════════════════════════════════════

import React, { useState } from 'react';
import api from '../utils/api';
import { XCircle, Send, RotateCcw, AlertCircle, CheckCircle2, Lock } from 'lucide-react';

// ───────────────────────────────────────────────────────────────
// BADGE de estado de firma
// ───────────────────────────────────────────────────────────────
export function BadgeEstadoFirma({ estado, esProveedor }) {
  const CONFIG = esProveedor ? {
    'borrador': { label: 'Borrador', color: '#6b7280', bg: '#f3f4f6' },
    'pendiente_cualificacion': { label: 'Pdte. cualificar', color: '#6b7280', bg: '#f3f4f6' },
    'pendiente_firma': { label: 'Pdte. firma', color: '#92400e', bg: '#fef3c7' },
    'cualificado': { label: '✓ Cualificado', color: '#065f46', bg: '#d1fae5' },
    'no_cualificado': { label: '✗ No cualificado', color: '#991b1b', bg: '#fee2e2' }
  } : {
    'borrador': { label: 'Borrador', color: '#6b7280', bg: '#f3f4f6' },
    'pendiente_firma': { label: 'Pdte. firma', color: '#92400e', bg: '#fef3c7' },
    'firmado': { label: '✓ Firmado', color: '#065f46', bg: '#d1fae5' },
    'rechazado': { label: '✗ Rechazado', color: '#991b1b', bg: '#fee2e2' }
  };
  
  const conf = CONFIG[estado] || { label: estado || 'Borrador', color: '#6b7280', bg: '#f3f4f6' };
  
  return (
    <span style={{ 
      display: 'inline-block', padding: '3px 9px', borderRadius: 12, 
      fontSize: '0.72rem', fontWeight: 600, color: conf.color, background: conf.bg,
      whiteSpace: 'nowrap'
    }}>
      {conf.label}
    </span>
  );
}

// ───────────────────────────────────────────────────────────────
// MODAL: Pasar a Pdte. firma (sin contraseña)
// ───────────────────────────────────────────────────────────────
export function ModalPasarPdteFirma({ endpoint, descripcion, onClose, onConfirmado, esProveedor }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const confirmar = async () => {
    setLoading(true); setError('');
    try {
      await api.put(endpoint);
      onConfirmado();
    } catch (err) {
      setError(err.response?.data?.error || 'Error al cambiar estado');
    } finally { setLoading(false); }
  };

  return (
    <div className="modal-overlay">
      <div className="modal">
        <div className="modal-header">
          <h2 style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#f59e0b' }}>
            <Send size={20} /> Pasar a Pendiente de Firma
          </h2>
          <button className="btn btn-ghost btn-icon" onClick={onClose}><XCircle size={18} /></button>
        </div>
        <div className="modal-body">
          <p>¿Marcar este registro como <strong>pendiente de {esProveedor ? 'cualificación' : 'firma'}</strong>?</p>
          
          {descripcion && (
            <div style={{ padding: 12, background: 'var(--color-gray-1)', borderRadius: 8, marginTop: 12, marginBottom: 12 }}>
              <div style={{ fontWeight: 600 }}>{descripcion}</div>
            </div>
          )}
          
          <div className="alert alert-warning" style={{ fontSize: '0.85rem' }}>
            <AlertCircle size={16} /> Después de marcar como pendiente, ve a <strong>Firmas Pendientes</strong> para enviar la solicitud a la Directora Técnica.
          </div>
          
          {error && <div className="alert alert-danger">{error}</div>}
        </div>
        <div className="modal-footer">
          <button className="btn btn-outline" onClick={onClose}>Cancelar</button>
          <button className="btn btn-primary" onClick={confirmar} disabled={loading}
            style={{ background: '#f59e0b', borderColor: '#f59e0b' }}>
            {loading ? <div className="spinner" style={{ width: 16, height: 16 }} /> : <Send size={16} />}
            Marcar como Pendiente
          </button>
        </div>
      </div>
    </div>
  );
}

// ───────────────────────────────────────────────────────────────
// MODAL: Revertir a Borrador (admin + contraseña)
// ───────────────────────────────────────────────────────────────
export function ModalRevertirBorrador({ endpoint, descripcion, onClose, onConfirmado }) {
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const confirmar = async () => {
    if (!password) { setError('Debes introducir tu contraseña'); return; }
    setLoading(true); setError('');
    try {
      await api.put(endpoint, { nuevo_estado: 'borrador', password });
      onConfirmado();
    } catch (err) {
      setError(err.response?.data?.error || 'Error al revertir');
    } finally { setLoading(false); }
  };

  return (
    <div className="modal-overlay">
      <div className="modal">
        <div className="modal-header">
          <h2 style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <RotateCcw size={20} color="#6b7280" /> Revertir a Borrador
          </h2>
          <button className="btn btn-ghost btn-icon" onClick={onClose}><XCircle size={18} /></button>
        </div>
        <div className="modal-body">
          <div className="alert alert-warning" style={{ fontSize: '0.85rem' }}>
            <AlertCircle size={16} /> Esto cancelará la solicitud de firma pendiente. El registro volverá a estado <strong>Borrador</strong> y podrá editarse libremente.
          </div>

          {descripcion && (
            <div style={{ padding: 12, background: 'var(--color-gray-1)', borderRadius: 8, marginBottom: 12 }}>
              <div style={{ fontWeight: 600 }}>{descripcion}</div>
            </div>
          )}

          <div className="form-group">
            <label className="form-label">🔐 Confirma con tu contraseña</label>
            <input type="password" className="form-control" value={password}
              onChange={e => setPassword(e.target.value)}
              placeholder="Tu contraseña actual"
              onKeyDown={e => e.key === 'Enter' && password && confirmar()} />
          </div>

          {error && <div className="alert alert-danger">{error}</div>}
        </div>
        <div className="modal-footer">
          <button className="btn btn-outline" onClick={onClose}>Cancelar</button>
          <button className="btn btn-primary" onClick={confirmar} disabled={loading || !password}>
            {loading ? <div className="spinner" style={{ width: 16, height: 16 }} /> : <RotateCcw size={16} />}
            Revertir a Borrador
          </button>
        </div>
      </div>
    </div>
  );
}

// ───────────────────────────────────────────────────────────────
// Helper: estado "bloqueado" (firmado/cualificado)
// ───────────────────────────────────────────────────────────────
export function esEstadoFirmado(estado, esProveedor) {
  if (esProveedor) {
    return ['cualificado', 'no_cualificado'].includes(estado);
  }
  return ['firmado', 'rechazado'].includes(estado);
}

// Icono de bloqueado para mostrar en lugar de los botones cuando está firmado
export function IconoBloqueado({ estado }) {
  return (
    <span title={`Bloqueado (estado: ${estado}, AEMPS)`} 
      style={{ color: '#9ca3af', display: 'inline-flex', alignItems: 'center', padding: 4 }}>
      <Lock size={13} />
    </span>
  );
}
